import React from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import routes from "../../routes";
import { checkLoginStatus } from "../../store/selectors/loginSelector";
import { getApplicantInfo, getAuthToken } from "../../store/selectors/appConfig";
import { getProspectId } from "../../store/selectors/appConfig";

import {
  isOtpVerified as getIsOtpVerified,
  isOtpGenerated as getIsOtpGenerated,
  getIsGenerating
} from "../../store/selectors/otp";
import { financeTypeURIs } from "../../constants";

export const ProtectedRoute = ({ component: Component, ...rest }) => {
  // Your authentication logic here
  const isAuthenticated = true; // Replace with actual authentication check

  return isAuthenticated ? <Component {...rest} /> : <Navigate to="/login" />;
};

/**
 * Protected route that validates finance type parameter
 * Ensures the finance type in URL is valid before rendering the component
 *
 * @param {Object} props Component props
 * @returns {JSX.Element} Protected route component that checks finance type validity
 */
export const FinanceTypeProtectedRoute = ({ component: Component, ...rest }) => {
  const { financeType } = useParams();
  const location = useLocation();

  if (!Object.keys(financeTypeURIs).includes(financeType)) {
    return <Navigate to={routes.financeProductComparison} state={{ from: location }} replace />;
  }

  return <Component {...rest} />;
};

export const ProspectProtectedRoute = ({ component: Component, ...rest }) => {
  const isAgent = useSelector(checkLoginStatus);
  const prospectId = useSelector(getProspectId);

  if (prospectId) {
    return <Component {...rest} />;
  }

  // For customer-facing routes, always redirect to the start page when no prospect ID exists
  const currentPath = window.location.pathname;
  const isCustomerRoute = currentPath.startsWith("/business/finances/");

  if (isCustomerRoute) {
    return <Navigate to={routes.comeBackLogin} replace />;
  }

  if (isAgent) {
    return <Navigate to={routes.login} />;
  }

  return <Navigate to={routes.comeBackLogin} />;
};

export const OTPGeneratedProtectedRoute = ({ component: Component, ...rest }) => {
  const isOtpGenerated = useSelector(getIsOtpGenerated);
  const isOtpVerified = useSelector(getIsOtpVerified);
  const isOtpGenerating = useSelector(getIsGenerating);
  if (
    process.env.REACT_APP_OTP_ENABLE === "N" ||
    isOtpGenerated ||
    isOtpVerified ||
    isOtpGenerating
  ) {
    return <Component {...rest} />;
  }

  return <Navigate to={routes.comeBackLogin} />;
};

export const OTPProtectedRoute = ({ component: Component, ...rest }) => {
  // Your OTP validation protection logic
  const isOtpVerified = useSelector(getIsOtpVerified);
  const isOtpGenerated = useSelector(getIsOtpGenerated);

  if (isOtpVerified || (process.env.REACT_APP_OTP_ENABLE === "N" && isOtpGenerated)) {
    return <Component {...rest} />;
  }

  return <Navigate to={routes.comeBackLogin} />;
};

export const ApplicantProtectedRoute = ({ component: Component, ...rest }) => {
  // Your applicant protection logic
  const isAgent = useSelector(checkLoginStatus);

  const { email = "", mobileNo = "", tradeLicenseNo = "" } = useSelector(getApplicantInfo);

  if (email && mobileNo && tradeLicenseNo) {
    return <Component {...rest} />;
  }

  if (isAgent) {
    return <Navigate to={routes.login} />;
  }

  return <Navigate to={routes.financeProductComparison} />;
};

export const AgentProtectedRoute = ({ component: Component, ...rest }) => {
  const isAuthenticated = useSelector(checkLoginStatus);
  const authToken = useSelector(getAuthToken);

  const isAgentValid = isAuthenticated && authToken;

  return isAgentValid ? <Component {...rest} /> : <Navigate to={routes.login} />;
};
